import React from 'react';
import { GRAY_500, GRAY_900 } from 'theme/grayScale';
import { COLOR_INFO_TEXT } from 'theme/colors';
import { stripHtmlTags } from '../../utils/renderHtml';

const PREVIEW_COUNT = 5;

/**
 * Renders a label/value list row where the value is itself a list — first 5
 * items shown, "+N more" reveals the rest with a "Show less" button to
 * collapse back. Generalized from CASRegistryPanel's original SynonymsList
 * (issue #585 Task 9.2) so NDCPanel's active_ingredients list reuses the
 * same pattern instead of a new list-UI component.
 *
 * `isHtml` strips HTML markup before display — CAS-source synonym strings
 * can contain tags (e.g. <em>) that would otherwise render as literal text.
 */
const ExpandableFieldList: React.FC<{
  label: string;
  items: string[];
  isHtml?: boolean;
}> = ({ label, items, isHtml = false }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (items.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '4px 0',
          fontSize: '14px',
          lineHeight: '1.4',
        }}>
        <span
          style={{
            minWidth: '140px',
            fontWeight: 600,
            color: GRAY_500,
            flexShrink: 0,
          }}>
          {label}
        </span>
        <span style={{ color: GRAY_900 }}>—</span>
      </div>
    );
  }

  const collapsible = items.length > PREVIEW_COUNT;
  const visible =
    expanded || !collapsible ? items : items.slice(0, PREVIEW_COUNT);
  const hiddenCount = items.length - PREVIEW_COUNT;

  const toggleStyle: React.CSSProperties = {
    marginTop: '4px',
    fontSize: '12px',
    fontWeight: 600,
    color: COLOR_INFO_TEXT,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        padding: '4px 0',
        fontSize: '14px',
        lineHeight: '1.4',
      }}>
      <span
        style={{
          minWidth: '140px',
          fontWeight: 600,
          color: GRAY_500,
          flexShrink: 0,
        }}>
        {label}
      </span>
      <div style={{ flex: 1 }}>
        {visible.map((item, index) => (
          <div
            key={`${item}-${index}`}
            style={{
              color: GRAY_900,
              wordBreak: 'break-word',
              paddingBottom: '2px',
            }}>
            {isHtml ? stripHtmlTags(item) : item}
          </div>
        ))}
        {collapsible && !expanded && (
          <button
            type="button"
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            style={toggleStyle}>
            +{hiddenCount} more
          </button>
        )}
        {collapsible && expanded && (
          <button
            type="button"
            aria-expanded={true}
            onClick={() => setExpanded(false)}
            style={toggleStyle}>
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpandableFieldList;
